import dayjs from "dayjs";
import { Calendar, CalendarStyles, Popover } from "frost-ui-react";
import React, { useEffect, useRef, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { DATE_TIME_FORMAT_TYPES } from "../../../configs/constants/DateTimeFormatTypes";
import useOutsideAlerter from "../../../hooks/useOutsideAlerter";
import { appTheme } from "../../../theme/Theme";
import { getBrowserLocale } from "../../../utils/date.time.utils";
import Flex from "../../Layout/Flex";
import Divider from "../../Shared/Divider/Divider";
import AppInput, { AppInputProps } from "../AppInput/AppInput";
import AppText from "../AppText/AppText";
import Button from "../Button/Button";

export type DateRange = {
	start: Date;
	end: Date;
};

interface AppDateTimePickerProps
	extends Pick<
		AppInputProps,
		"placeholder" | "label" | "margin" | "containerMargin" | "containerWidth" | "mandatory"
	> {
	onChange?: (date: Date) => any;
	value?: Date;
	defaultTime?: string;
	renderInput?: (inputValue: string, onOpen: Function) => React.ReactElement;
}

const AppDateTimePicker: React.FC<AppDateTimePickerProps> = ({
	onChange,
	value,
	defaultTime = "12:00",
	renderInput,
	...inputProps
}) => {
	const { t } = useTranslation();
	const [hasFocus, setFocus] = useState(false);
	const [inputValue, setInputValue] = useState(dateValueToString(value));
	const [dateValue, setDateValue] = useState(value);
	const [timeValue, setTimeValue] = useState(
		value ? dayjs(value).format(DATE_TIME_FORMAT_TYPES.HOURS_MINUTES) : defaultTime
	);

	const wrapperRef = useRef(null);
	useOutsideAlerter(wrapperRef, close, "mousedown", [close]);

	function handleChange(date?: Date) {
		const hhMm = timeStringToHhMm(timeValue);
		if (date && hhMm) {
			date = setHoursAndMinutes(date, hhMm || defaultTime);
		}
		setDateValue(date);
	}

	function close() {
		setFocus(false);

		// Revert back to orig value if no complete range is selected when closed
		if (!dateValue) {
			setDateValue(value);
		}
	}

	useEffect(() => {
		const hhMm = timeStringToHhMm(timeValue);

		if (hhMm && dateValue) {
			setDateValue(setHoursAndMinutes(dateValue, hhMm));
		}
	}, [timeValue]);

	function handleSubmit() {
		setInputValue(dateValueToString(dateValue));
		onChange && dateValue && onChange(dateValue);
		close();
	}

	return (
		<Wrapper ref={wrapperRef}>
			{renderInput ? (
				renderInput(inputValue, () => setFocus(true))
			) : (
				<AppInput
					icon="calendar"
					onFocus={() => setFocus(true)}
					value={inputValue}
					readOnly={true}
					{...inputProps}
				/>
			)}
			{hasFocus && (
				<CalendarWrapper top={inputProps.label ? "5rem" : "2rem"}>
					<Popover pointTo="top" styles={{ width: 350, offset: "2rem" }}>
						<Flex direction="column" gap="1.5rem">
							<Calendar
								onChange={handleChange}
								value={dateValue}
								locale={getBrowserLocale()}
								styles={calendarStyle}
								focusOnDate={dateValue || new Date()}
								canSelectMonth
							/>
							<Divider width={1} margin="0" />
							<div>
								<AppInput
									className="input"
									label={"datePicker.time"}
									containerWidth="100px"
									placeholder="datePicker.timePlaceholder"
									icon="clock-ash"
									onChangeText={setTimeValue}
									value={timeValue}
									containerMargin="0 0 0.75rem 0"
									onEnter={handleSubmit}
								/>

								<Flex
									cursor="pointer"
									direction="row"
									flexWrap="wrap"
									gap="7px"
									alignItems="center"
								>
									<AppText color="grayTextLight">{t("datePicker.timeShortcuts")}:</AppText>
									{[8, 9, 10, 11, 12, 13, 14, 15, 16, 17].map((val) => (
										<Flex key={val} direction="row" gap={"7px"} width="fit-content">
											<AppText color="actionBlue" onClick={() => setTimeValue(val + ":00")}>
												{val}:00
											</AppText>
											<AppText color="actionBlue" onClick={() => setTimeValue(val + ":30")}>
												{val}:30
											</AppText>
										</Flex>
									))}
								</Flex>
							</div>

							<Button backgroundColor="secondary" onClick={handleSubmit}>
								{t("datePicker.select")}
							</Button>
						</Flex>
					</Popover>
				</CalendarWrapper>
			)}
		</Wrapper>
	);
};

export default AppDateTimePicker;

const Wrapper = styled.div`
	position: relative;
	min-width: 240px;
`;

const CalendarWrapper = styled.div<{ top: string }>`
	position: absolute;
	/* Above sticky tab bars (Tailwind z-20), below layout overlays (z-index: 100). */
	z-index: 30;
	top: ${(props) => props.top};

	.input {
		font-size: 24px;
	}
`;

const calendarStyle: CalendarStyles = {
	circleFontSizeRatio: 1.8,
	dayFontSize: 14,
	headerFontSize: 14,
	weekDayFontSize: 12,
	highlightStyles: {
		value: {
			backgroundColor: appTheme.colors.COLOR_ACTION_BLUE,
			color: appTheme.colors.COLOR_WHITE,
			borderColor: appTheme.colors.COLOR_DARK,
		},
	},
};

function dateValueToString(date?: Date) {
	if (!date) {
		return "";
	}

	return dayjs(date).format(DATE_TIME_FORMAT_TYPES.DATE_TIME);
}

function timeStringToHhMm(val: string) {
	val = val.replace(/[^0-9]/g, "");

	if (val.length === 3) {
		let hh = val.substring(0, 1);
		let mm = val.substring(1, 3);

		if (Number(hh) > 23) {
			hh = "00";
		}

		if (Number(mm) > 59) {
			mm = "00";
		}

		return "0" + hh + ":" + mm;
	} else if (val.length === 4) {
		let hh = val.substring(0, 2);
		let mm = val.substring(2, 4);

		if (Number(hh) > 23) {
			hh = "00";
		}

		if (Number(mm) > 59) {
			mm = "00";
		}

		return hh + ":" + mm;
	}
}

function setHoursAndMinutes(date: Date, hhMm: string) {
	const [hh, mm] = hhMm.split(":");
	return dayjs(date).set("hours", Number(hh)).set("minutes", Number(mm)).toDate();
}
